import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Zap, 
  Target,
  TrendingUp,
  TrendingDown,
  Brain,
  Loader2,
  Clock,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Predictions = () => {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState({});
  const [loadingPrediction, setLoadingPrediction] = useState(null);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/games?status=scheduled`);
      setGames(response.data);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrediction = async (gameId) => {
    if (!user) {
      toast.error('Please login to get AI predictions');
      return;
    }

    setLoadingPrediction(gameId);
    try {
      const response = await axios.post(`${API}/predictions`, { game_id: gameId });
      setPredictions(prev => ({ ...prev, [gameId]: response.data }));
      toast.success('Prediction generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get prediction');
    } finally {
      setLoadingPrediction(null);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'low': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="predictions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">AI PREDICTIONS</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            GPT-5.2 powered game predictions based on team performance
          </p>
        </div>
        <Badge variant="outline" className="mt-4 md:mt-0 w-fit">
          <Brain className="w-4 h-4 mr-1" />
          Powered by OpenAI
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5" data-testid="info-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-bold">How it works</h3>
              <p className="text-sm text-muted-foreground">
                Our AI analyzes team statistics, win rates, point differentials, and historical performance 
                to predict game outcomes. The more games played, the better the predictions!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Games List */}
      {games.length > 0 ? (
        <div className="space-y-6">
          {games.map((game, index) => {
            const prediction = predictions[game.id];
            
            return (
              <Card 
                key={game.id}
                className="overflow-hidden"
                data-testid={`prediction-game-${index}`}
              >
                <div className="h-1 bg-gradient-to-r from-primary to-accent" />
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{game.round}</Badge>
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDateTime(game.scheduled_time)}
                      </span>
                    </div>
                    {!prediction && (
                      <Button
                        onClick={() => getPrediction(game.id)}
                        disabled={loadingPrediction === game.id || !user}
                        className="btn-primary gap-2"
                        data-testid={`get-prediction-btn-${index}`}
                      >
                        {loadingPrediction === game.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Brain className="w-4 h-4" />
                            Get Prediction
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Teams */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg border ${
                      prediction?.predicted_winner === game.team1_name 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'bg-muted/50'
                    }`}>
                      <p className="font-bold text-lg">{game.team1_name}</p>
                      {prediction && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            {prediction.team1_win_probability >= prediction.team2_win_probability ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            )}
                            <span className="font-mono text-xl font-bold">
                              {prediction.team1_win_probability}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className={`p-4 rounded-lg border ${
                      prediction?.predicted_winner === game.team2_name 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'bg-muted/50'
                    }`}>
                      <p className="font-bold text-lg">{game.team2_name}</p>
                      {prediction && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            {prediction.team2_win_probability >= prediction.team1_win_probability ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            )}
                            <span className="font-mono text-xl font-bold">
                              {prediction.team2_win_probability}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Prediction Details */}
                  {prediction && (
                    <div className="p-4 rounded-lg border bg-muted/30" data-testid={`prediction-result-${index}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-primary" />
                          <span className="font-bold">AI Prediction</span>
                        </div>
                        <Badge variant="outline" className={getConfidenceColor(prediction.confidence)}>
                          {prediction.confidence} Confidence
                        </Badge>
                      </div>
                      
                      <div className="mb-3">
                        <span className="text-muted-foreground">Predicted Winner: </span>
                        <span className="font-bold text-green-500">{prediction.predicted_winner}</span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">{prediction.analysis}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="text-center py-12" data-testid="no-games">
          <CardContent>
            <Brain className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Upcoming Games</h3>
            <p className="text-muted-foreground">
              Check back later for games to predict
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
